import { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { Save, Sparkles, Download, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';

export function TranslationsEditor({ locales, onChanged }) {
    const { builtin, refresh } = useI18n();
    const allBuiltinKeys = useMemo(() => Object.keys(builtin?.en || {}).sort(), [builtin]);

    const [target, setTarget] = useState('ca');
    const [filter, setFilter] = useState('');
    const [edits, setEdits] = useState({}); // key -> string
    const [saving, setSaving] = useState(false);
    const [autoBusy, setAutoBusy] = useState(false);

    // Source = English built-in (best fallback) for display
    const source = builtin?.en || {};
    // Existing DB overrides for target
    const overrides = useMemo(() => {
        const l = (locales || []).find((x) => x.code === target);
        return (l && l.translations) || {};
    }, [locales, target]);

    // When switching target, reset edits to current overrides (so user can edit)
    useEffect(() => {
        setEdits({ ...overrides });
    }, [target, locales]);

    const isBuiltin = (locales || []).find((x) => x.code === target)?.builtin;

    const keys = useMemo(() => {
        const merged = new Set([...allBuiltinKeys, ...Object.keys(overrides), ...Object.keys(edits)]);
        let arr = Array.from(merged).sort();
        if (filter.trim()) {
            const q = filter.toLowerCase();
            arr = arr.filter((k) => {
                const sv = (source[k] || '').toLowerCase();
                const tv = (edits[k] ?? overrides[k] ?? '').toLowerCase();
                return k.toLowerCase().includes(q) || sv.includes(q) || tv.includes(q);
            });
        }
        return arr;
    }, [allBuiltinKeys, overrides, edits, filter, source]);

    const builtinValue = (key) => (builtin?.[target] || {})[key];

    const save = async () => {
        setSaving(true);
        try {
            // Only send keys whose value differs from current overrides (incl. removals via empty string)
            const diff = {};
            const allKeys = new Set([...Object.keys(edits), ...Object.keys(overrides)]);
            for (const k of allKeys) {
                const newVal = edits[k] ?? '';
                const oldVal = overrides[k] ?? '';
                if (newVal !== oldVal) diff[k] = newVal;
            }
            if (Object.keys(diff).length === 0) {
                toast.info('Sense canvis');
                return;
            }
            const r = await api.post('/admin/i18n/save', { code: target, translations: diff });
            toast.success(`Desat (${r.data.keys} claus actives)`);
            await refresh();
            if (onChanged) await onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        } finally { setSaving(false); }
    };

    const downloadCurrent = () => {
        // Build merged dict (built-in + edits + overrides) for download
        const merged = { ...(builtin?.[target] || {}), ...overrides, ...edits };
        const payload = {
            code: target,
            name: (locales.find((x) => x.code === target) || {}).name || target,
            translations: merged,
            exported_at: new Date().toISOString(),
            note: 'Albambam translations export. Re-upload as JSON via Admin → Languages.',
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `albambam-${target}.json`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const resetKey = (k) => {
        setEdits((e) => {
            const next = { ...e };
            delete next[k];
            return next;
        });
    };

    const autoTranslate = async () => {
        if (!confirm(`Auto-translate all ~${allBuiltinKeys.length} keys to "${target}" using AI? Existing edits will be merged.`)) return;
        setAutoBusy(true);
        try {
            const r = await api.post('/admin/i18n/autotranslate', {
                code: target,
                name: (locales.find((x) => x.code === target) || {}).name,
                source: source,
                source_lang: 'English',
            });
            toast.success(`AI translated ${r.data.translated} keys`);
            await refresh();
            if (onChanged) await onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        } finally { setAutoBusy(false); }
    };

    return (
        <Card data-testid="translations-editor" className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-4">
                <div className="flex flex-col sm:flex-row gap-2 items-end">
                    <div>
                        <Label>Idioma</Label>
                        <Select value={target} onValueChange={setTarget}>
                            <SelectTrigger data-testid="editor-target-select" className="w-44 bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)] font-mono-tab">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(locales || []).map((l) => (
                                    <SelectItem key={l.code} value={l.code}>
                                        {l.code.toUpperCase()} · {l.name} {l.builtin ? '' : '(custom)'}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <Label>Cerca clau / valor</Label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--alb-text-muted)]" />
                            <Input data-testid="editor-search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="nav.home, Welcome..." className="pl-9 bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-[color:var(--alb-surface-2)]">{isBuiltin ? 'Integrat' : 'Personalitzat'}</Badge>
                    <Button data-testid="editor-download" onClick={downloadCurrent} variant="outline" className="border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface-2)]"><Download className="h-4 w-4 mr-1" /> Descarrega JSON</Button>
                    <Button data-testid="editor-autotranslate" onClick={autoTranslate} disabled={autoBusy} variant="outline" className="border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface-2)]"><Sparkles className="h-4 w-4 mr-1 text-[color:var(--alb-pink)]" /> {autoBusy ? 'AI...' : 'Auto-tradueix (AI)'}</Button>
                    <Button data-testid="editor-save" onClick={save} disabled={saving} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]"><Save className="h-4 w-4 mr-1" /> {saving ? '...' : 'Desa canvis'}</Button>
                </div>
            </div>

            <div className="text-xs text-[color:var(--alb-text-muted)] mb-3">
                {keys.length} claus mostrades. Deixa el camp buit per fer servir el valor integrat.
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin" data-testid="editor-rows">
                {keys.map((k) => {
                    const built = builtinValue(k) ?? source[k];
                    const value = edits[k] !== undefined ? edits[k] : (overrides[k] ?? '');
                    const edited = edits[k] !== undefined && edits[k] !== (overrides[k] ?? '');
                    const isOverride = overrides[k] !== undefined;
                    return (
                        <div key={k} className={`grid grid-cols-12 gap-2 items-start py-2 border-b border-[color:var(--alb-hairline)]/40 ${edited ? 'bg-[rgba(244,200,74,0.06)]' : ''}`}>
                            <div className="col-span-12 md:col-span-3">
                                <div className="font-mono-tab text-xs text-[color:var(--alb-sun)] break-all">{k}</div>
                                <div className="text-[10px] text-[color:var(--alb-text-muted)] mt-1">{isOverride ? 'override' : 'integrat'}</div>
                            </div>
                            <div className="col-span-12 md:col-span-4">
                                <div className="text-[10px] uppercase text-[color:var(--alb-text-muted)] mb-1">EN</div>
                                <div className="text-xs text-[color:var(--alb-text-muted)] line-clamp-3">{source[k]}</div>
                                {built && built !== source[k] && (
                                    <div className="text-xs text-[color:var(--alb-aqua)] line-clamp-3 mt-1">{target.toUpperCase()}: {built}</div>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-5">
                                <div className="text-[10px] uppercase text-[color:var(--alb-text-muted)] mb-1">{target.toUpperCase()} ·  edita aquí</div>
                                <div className="flex items-start gap-1">
                                    <Input
                                        data-testid={`editor-input-${k}`}
                                        value={value}
                                        onChange={(e) => setEdits((p) => ({ ...p, [k]: e.target.value }))}
                                        placeholder={built || source[k] || ''}
                                        className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)] text-sm"
                                    />
                                    {edits[k] !== undefined && (
                                        <Button size="icon" variant="ghost" onClick={() => resetKey(k)} title="Descarta edició"><RotateCcw className="h-3 w-3" /></Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {keys.length === 0 && <div className="text-center text-[color:var(--alb-text-muted)] py-8">Cap clau coincideix.</div>}
            </div>
        </Card>
    );
}
